import { useEffect, useRef, useState } from 'react';

export default function DeferredPreviewVideo({ src, poster, className }: {
  src: string;
  poster: string;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!('IntersectionObserver' in window)) {
      setIsNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsNearViewport(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isNearViewport) return;

    let isVisible = false;
    const syncPlayback = () => {
      if (isVisible && document.visibilityState !== 'hidden') {
        void video.play().catch(() => undefined);
      } else {
        video.pause();
      }
    };

    // Load the poster nearby, but only download and decode the video while it
    // is visible. Pause previously viewed cards and background browser tabs.
    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(([entry]) => {
          isVisible = entry.isIntersecting;
          syncPlayback();
        })
      : undefined;

    if (observer) {
      observer.observe(video);
    } else {
      isVisible = true;
      syncPlayback();
    }
    document.addEventListener('visibilitychange', syncPlayback);

    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', syncPlayback);
      video.pause();
    };
  }, [isNearViewport]);

  return (
    <video
      ref={videoRef}
      src={isNearViewport ? src : undefined}
      poster={isNearViewport ? poster : undefined}
      className={className}
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
    />
  );
}
